
import { Shield, FileCheck, BarChart3, AlertTriangle, Clock, Zap } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const IncidentResponseRemediation = () => {
  return (
    <LandingPageTemplate
      title="Incident Response & Remediation"
      description="Expert incident response and remediation services to help organizations recover from security incidents and strengthen their security posture."
      keywords={[
        "incident response",
        "security remediation",
        "breach recovery",
        "security incident",
        "cyber attack response"
      ]}
      canonicalUrl="/cybersecurity/incident-response-remediation/"
      schemaType="service"
      schemaData={{
        name: "Incident Response and Remediation Services",
        description: "Expert incident response and remediation support",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Rapid Response"
        highlighted="Incident Recovery"
        description="Expert incident response and remediation services to help your organization recover from security incidents and prevent future occurrences."
        ctaText="Get Emergency Support"
        heroPattern="dots"
      />

      <FeatureSection 
        title="Comprehensive Incident Response"
        subtitle="KEY FEATURES"
        description="Our incident response and remediation services provide immediate support and long-term security improvements."
        features={[
          {
            title: "24/7 Response",
            description: "Round-the-clock emergency response team availability.",
            icon: <Clock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Rapid Assessment",
            description: "Quick evaluation of incident scope and impact.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Containment",
            description: "Immediate actions to contain and control the incident.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Investigation",
            description: "Thorough investigation to determine root cause.",
            icon: <FileCheck className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Recovery",
            description: "Guided recovery process to restore operations.",
            icon: <Zap className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Impact Analysis",
            description: "Assessment of incident impact and damage scope.",
            icon: <BarChart3 className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection
        title="Our Response Approach"
        subtitle="METHODOLOGY"
        description="A systematic approach to incident response and remediation"
        steps={[
          {
            title: "Initial Response",
            description: "Immediate response and situation assessment"
          },
          {
            title: "Containment",
            description: "Implement measures to contain the incident"
          },
          {
            title: "Investigation",
            description: "Thorough analysis of incident cause and scope"
          },
          {
            title: "Remediation",
            description: "Execute recovery and remediation plan"
          },
          {
            title: "Prevention",
            description: "Implement measures to prevent recurrence"
          }
        ]}
      />

      <BenefitSection
        title="Why Choose Our Incident Response"
        benefits={[
          {
            title: "24/7 Availability",
            description: "Round-the-clock emergency response support"
          },
          {
            title: "Expert Team",
            description: "Experienced incident response specialists"
          },
          {
            title: "Rapid Response",
            description: "Quick deployment and immediate action"
          },
          {
            title: "Complete Recovery",
            description: "Full service from response to recovery"
          },
          {
            title: "Future Prevention",
            description: "Long-term security improvements"
          },
          {
            title: "Documentation",
            description: "Detailed incident documentation and reporting"
          }
        ]}
      />

      <CtaSection 
        title="Need Emergency Response Support?"
        description="Contact our incident response team immediately for expert assistance."
        ctaText="Get Emergency Support"
        background="purple"
        modalTitle="Request Incident Response"
        modalDescription="Tell us about your security incident for immediate support."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Incident Response",
          service_interest: "Emergency Support"
        }}
      />
    </LandingPageTemplate>
  );
};

export default IncidentResponseRemediation;
